import { useEvento } from "@/store/evento/evento";
import { releaseEntradas, reserveEntradas } from "@/lib/services/eventos";
import { EntradaSeleccionada } from "@/schemas/checkout.schema";
import { reserveLifecycle } from "./reserveLifecycle";
import { logInfo } from "@/services/log.service";

export const useReserve = () => {
    const { state: reservaId, set: setReservaId } = useEvento.use.reserva();

    const { state: funcion } = useEvento.use.funcion();

    const reserve = async (entradas: EntradaSeleccionada[], signal?: AbortSignal) => {
        // Verificar si la operación fue cancelada antes de iniciar
        if (signal?.aborted) {
            throw new DOMException('Operación cancelada', 'AbortError');
        }

        try {
            const reserva = await reserveEntradas(funcion?.id as string, entradas);

            // Verificar si fue cancelada después de la llamada
            if (signal?.aborted) {
                throw new DOMException('Operación cancelada', 'AbortError');
            }

            if (!reserva.reservaId) {
                throw new Error('Error al reservar entradas: no se recibió ID de reserva');
            }
            
            logInfo({
                message: 'Reserva EXITOSA (Frontend tracking)',
                reservaId: reserva.reservaId,
                funcionId: funcion?.id
            });

            // Nueva reserva: limpiar todos los flags del ciclo anterior.
            reserveLifecycle.reset();
            setReservaId(reserva.reservaId);
        } catch (error) {
            // Si fue cancelada, re-lanzar el error sin modificar
            if (error instanceof DOMException && error.name === 'AbortError') {
                throw error;
            }
            // Para otros errores, proporcionar mensaje más descriptivo
            throw new Error(
                error instanceof Error
                    ? `Error al reservar entradas: ${error.message}`
                    : 'Error desconocido al reservar entradas'
            );
        }
    }

    const release = async (signal?: AbortSignal) => {
        // Verificar si la operación fue cancelada antes de iniciar
        if (signal?.aborted) {
            throw new DOMException('Operación cancelada', 'AbortError');
        }

        // No intentar liberar si no hay reserva
        if (!reservaId) {
            return;
        }

        // Guard: evitar múltiples releases (simultáneos o duplicados)
        if (reserveLifecycle.is('releasing')) {
            console.warn('[useReserve] Release ya en progreso o completado, ignorando');
            return;
        }

        // Marcar inmediatamente para bloquear otras llamadas y beforeunload
        reserveLifecycle.set('releasing');

        try {
            const isSuccess = await releaseEntradas(funcion?.id as string, reservaId as string);

            // Verificar si fue cancelada después de la llamada
            if (signal?.aborted) {
                throw new DOMException('Operación cancelada', 'AbortError');
            }

            if (!isSuccess) {
                throw new Error('Error al liberar entradas: operación falló');
            }

            setReservaId(null);
        } catch (error) {
            // Si falló, permitir reintentos
            reserveLifecycle.set('idle');

            // Si fue cancelada, re-lanzar el error sin modificar
            if (error instanceof DOMException && error.name === 'AbortError') {
                throw error;
            }
            // Para otros errores, proporcionar mensaje más descriptivo
            throw new Error(
                error instanceof Error
                    ? `Error al liberar entradas: ${error.message}`
                    : 'Error desconocido al liberar entradas'
            );
        }
    }


    return {
        reserve,
        release,
        setReservaId,
        reservaId,
    }
}
